import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  APIProvider, Map, AdvancedMarker, Pin,
} from '@vis.gl/react-google-maps';
import {
  Package, Truck, CheckCircle2, Clock, MapPin, ArrowRight, RefreshCw, ShoppingBag, ChefHat, ReceiptText,
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Ordered pipeline of statuses shown in the timeline. Statuses received from
// the backend that fall in this list light up as done / current.
const PIPELINE = [
  { key: 'pending_payment', label: 'Order placed', icon: ShoppingBag },
  { key: 'confirmed',       label: 'Confirmed',   icon: CheckCircle2 },
  { key: 'preparing',       label: 'Preparing',   icon: ChefHat },
  { key: 'dispatched',      label: 'Dispatched',  icon: Truck },
  { key: 'en_route',        label: 'On the way',  icon: Truck },
  { key: 'delivered',       label: 'Delivered',   icon: CheckCircle2 },
];

const RWF = (n) => new Intl.NumberFormat('en-RW', { maximumFractionDigits: 0 }).format(n || 0);
const fmtTs = (iso) => (!iso ? '' : new Date(iso).toLocaleString());

export default function PublicOrderTrackingPage() {
  const { orderNumber } = useParams();
  const [order, setOrder] = useState(null);
  const [rider, setRider] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const pollRef = useRef(null);

  const load = React.useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/shop/track/${orderNumber}`);
      setOrder(r.data);
      setError('');
      // Poll rider location only when out for delivery
      if (r.data.status === 'dispatched' || r.data.status === 'en_route') {
        const rr = await axios.get(`${API_URL}/shop/track/${orderNumber}/rider-location`);
        setRider(rr.data);
      } else {
        setRider(null);
      }
    } catch (err) {
      setError(err?.response?.status === 404 ? 'We couldn\'t find that order.' : 'Unable to load tracking info.');
    } finally { setLoading(false); }
  }, [orderNumber]);

  useEffect(() => { load(); }, [load]);

  // Auto-poll every 20s while the order is active
  useEffect(() => {
    if (!order) return () => {};
    const active = order.status && order.status !== 'delivered' && order.status !== 'cancelled';
    if (!active) return () => {};
    pollRef.current = setInterval(load, 20000);
    return () => pollRef.current && clearInterval(pollRef.current);
  }, [order, load]);

  const stepsWithState = () => {
    if (!order) return [];
    const doneStatuses = new Set((order.status_history || []).map((s) => s.status));
    let currentReached = false;
    return PIPELINE.map((step) => {
      const done = doneStatuses.has(step.key);
      const isCurrent = !currentReached && order.status === step.key;
      if (isCurrent) currentReached = true;
      return { ...step, done, isCurrent };
    });
  };

  if (loading) {
    return <PublicShell><div className="text-center py-20 text-muted-foreground" data-testid="track-loading">Loading tracking…</div></PublicShell>;
  }

  if (error) {
    return (
      <PublicShell>
        <div className="text-center py-16" data-testid="track-not-found">
          <Package className="w-14 h-14 mx-auto text-muted-foreground/40 mb-4" />
          <h1 className="text-2xl font-serif font-bold">{error}</h1>
          <p className="text-sm text-muted-foreground mt-2">Double-check the order number and try again.</p>
          <Link to="/shop" className="inline-flex items-center gap-1.5 mt-6 text-primary hover:underline">
            Shop online <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </PublicShell>
    );
  }

  const steps = stepsWithState();
  const delivered = order.status === 'delivered';

  return (
    <PublicShell>
      <div className="max-w-3xl mx-auto px-4 py-10" data-testid="track-root">
        {/* Header */}
        <div className="text-center mb-8">
          <p className="text-sm uppercase tracking-wider text-muted-foreground">Order tracking</p>
          <h1 className="text-3xl font-serif font-bold mt-1" data-testid="track-order-number">Order #{order.order_number}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Hi <strong>{order.customer_name}</strong> — here&apos;s where your order is right now.
          </p>
        </div>

        {/* Status banner */}
        <div className={`rounded-2xl border p-5 mb-8 ${delivered ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`} data-testid="track-status-banner">
          <div className="flex items-center gap-3">
            {delivered ? <CheckCircle2 className="w-7 h-7 text-emerald-600" /> : <Clock className="w-7 h-7 text-amber-600" />}
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Current status</div>
              <div className="text-xl font-semibold" data-testid="track-status-label">
                {(steps.find((s) => s.isCurrent) || steps[steps.length - 1]).label}
              </div>
              {rider && rider.available && rider.eta_minutes != null && (
                <div className="text-sm text-emerald-700 mt-0.5">
                  Rider ETA: <strong>~{rider.eta_minutes} min</strong>
                  {rider.distance_km != null && <> · {rider.distance_km} km away</>}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Timeline */}
        <div className="bg-white rounded-2xl border border-border p-5 mb-8" data-testid="track-timeline">
          <h2 className="font-semibold mb-4">Delivery timeline</h2>
          <ol className="relative border-s-2 border-border ml-3">
            {steps.map((step) => {
              const Icon = step.icon;
              const cls = step.done
                ? 'bg-emerald-500 text-white'
                : step.isCurrent
                  ? 'bg-amber-500 text-white ring-4 ring-amber-100 animate-pulse'
                  : 'bg-muted text-muted-foreground';
              const tsEntry = (order.status_history || []).find((s) => s.status === step.key);
              return (
                <li key={step.key} className="mb-6 ms-6" data-testid={`track-step-${step.key}`}>
                  <span className={`absolute -start-4 flex items-center justify-center w-8 h-8 rounded-full ${cls}`}>
                    <Icon className="w-4 h-4" />
                  </span>
                  <div className="flex items-baseline justify-between gap-2">
                    <div className={`font-medium ${step.done || step.isCurrent ? 'text-foreground' : 'text-muted-foreground'}`}>{step.label}</div>
                    {tsEntry && <div className="text-xs text-muted-foreground">{fmtTs(tsEntry.at)}</div>}
                  </div>
                </li>
              );
            })}
          </ol>
          <button
            onClick={load}
            className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline mt-2"
            data-testid="track-refresh-btn"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>

        {/* Rider map (only when out for delivery) */}
        {rider && rider.available && rider.location && MAPS_KEY && (
          <div className="bg-white rounded-2xl border border-border overflow-hidden mb-8" data-testid="track-map-card">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm">
                <Truck className="w-4 h-4 text-emerald-600" />
                <strong>Live rider location</strong>
              </div>
              <div className="text-xs text-muted-foreground">
                Updates automatically every 20 s
              </div>
            </div>
            <div style={{ height: 320 }}>
              <APIProvider apiKey={MAPS_KEY}>
                <Map
                  defaultCenter={{ lat: rider.location.lat, lng: rider.location.lng }}
                  defaultZoom={14}
                  mapId="bison-track-map"
                  gestureHandling="cooperative"
                  disableDefaultUI={false}
                >
                  <AdvancedMarker position={{ lat: rider.location.lat, lng: rider.location.lng }} title="Rider">
                    <Pin background="#2f5233" glyphColor="#fff" borderColor="#1a3020" />
                  </AdvancedMarker>
                </Map>
              </APIProvider>
            </div>
          </div>
        )}

        {/* Summary */}
        <div className="bg-white rounded-2xl border border-border p-5 mb-10" data-testid="track-summary">
          <h2 className="font-semibold mb-3">Order summary</h2>
          <dl className="grid grid-cols-2 gap-3 text-sm">
            <dt className="text-muted-foreground">Items</dt>
            <dd className="text-right">{order.item_count}</dd>
            <dt className="text-muted-foreground">Total</dt>
            <dd className="text-right"><strong>RWF {RWF(order.total)}</strong></dd>
            <dt className="text-muted-foreground">Payment</dt>
            <dd className="text-right capitalize">{(order.payment_method || '—').replace(/_/g, ' ')}</dd>
            <dt className="text-muted-foreground">Delivering to</dt>
            <dd className="text-right flex items-center justify-end gap-1"><MapPin className="w-3.5 h-3.5" />{order.delivery_area || '—'}</dd>
            {order.scheduled_for && (<>
              <dt className="text-muted-foreground">Scheduled for</dt>
              <dd className="text-right">{fmtTs(order.scheduled_for)}</dd>
            </>)}
            <dt className="text-muted-foreground">Placed</dt>
            <dd className="text-right">{fmtTs(order.placed_at)}</dd>
          </dl>
          {order.ebm_available && (
            <div className="mt-4 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900 flex items-start gap-2" data-testid="track-ebm-notice">
              <ReceiptText className="w-4 h-4 mt-0.5" />
              <div>Your EBM invoice is ready. Sign in to your account to download it — or check the email we sent you.</div>
            </div>
          )}
        </div>

        <div className="text-center text-xs text-muted-foreground pb-6">
          Questions? Call <a href="tel:+250791687658" className="text-primary hover:underline">+250 791 687 658</a> or WhatsApp us.
        </div>
      </div>
    </PublicShell>
  );
}

/**
 * Minimal public shell — matches the landing-page look but has no header/footer
 * dependency on private auth contexts. Keeps the tracking page perfectly
 * shareable via SMS / WhatsApp link.
 */
function PublicShell({ children }) {
  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50/40 to-white">
      <header className="bg-white border-b border-border">
        <div className="max-w-3xl mx-auto flex items-center justify-between px-4 py-3">
          <Link to="/" className="flex items-center gap-2" data-testid="track-home-link">
            <img src="/bison-logo.png" alt="Bison" className="h-8 w-auto" />
            <span className="font-serif font-bold text-lg">Bison Food Solutions</span>
          </Link>
          <Link to="/shop" className="text-sm text-primary hover:underline hidden sm:inline">Shop online →</Link>
        </div>
      </header>
      {children}
    </div>
  );
}
